import debounce from 'lodash/debounce'
import { loadViggo } from 'app/viggo'
import { create } from 'app/utils/data'
import { buildIconSvgMarkup } from 'app/views/icons/components/icons/details/utils'
import {
  MAX_LAYERS,
  PRIMARY,
  SECONDARY,
  layersInGroup,
  sortLayersByGroup,
  createLayer,
  createOperation,
  copyLayersInList,
  layerIdRange,
  orderedLayerIds,
  buildOutlineOperation,
  strokeOffsetForLayer,
  buildLibraryCutoutOperation,
  usesLibraryCutout,
  moveLayerInList,
  buildCombineOperations,
  buildSubtractOperation,
  combineWithFallback,
  shareNameFromLayers
} from 'app/store/modules/iconComposition'
import {
  DEFAULT_DRAFT_KEY,
  iconDraftKey,
  persistDraft,
  readDraft,
  clearDraft
} from 'app/store/modules/iconBuilderDrafts'

// Re-exported so existing importers of these symbols from this module keep
// working; their definitions now live in the extracted logic modules.
export { MAX_LAYERS, DEFAULT_DRAFT_KEY, iconDraftKey }

// Per-store debouncer, keyed off the store's `dispatch` reference so each Vuex
// instance gets its own closure. A module-level singleton would leak across
// hot-reloads and test runs.
const _debouncedRenderByDispatch = new WeakMap()

// How long the render debounce waits, and the longest it will go without
// drawing while input keeps arriving. Exported so the tests assert against the
// shipping value rather than a hand-copied one — the comment on the debounce
// below anticipates this being retuned, and a copy would drift silently.
// Both are the same number by design: see that comment for why the window has
// to be wider than a render.
export const RENDER_DEBOUNCE_MS = 700
export const RENDER_MAX_WAIT_MS = 700

// The deepest the undo history goes. Bounded because the history is persisted
// alongside the composition: a 20-layer record is ~16KB of mostly SVG path data,
// so whole-composition snapshots would cost ~16MB across the 21 drafts the wizard
// keeps, against a ~5MB origin budget. Entries are deltas for the same reason —
// see specs/2092-icon-wizard-undo/research.md R-01 for the measurements.
export const MAX_HISTORY = 50

// The operation types an arrow-key nudge writes. Snapping to a position preset
// clears them, since a preset and a nudge are two ways of saying where a layer
// sits and the preset wins.
const NUDGE_TYPES = ['containerUp', 'containerDown', 'containerLeft', 'containerRight']

// How each recorded change is reversed. Keyed by the entry's `kind`, so adding a
// new undoable change is a new row here rather than another branch in
// `undoLastAction`.
//
// An entry carries only what its inverse needs. It must never carry a layer's
// `path`, `iconName`, `prefix`, `width` or `height` — those are set once at
// creation and never mutated, so recording them is pure waste, and the storage
// budget above depends on them staying out.
const UNDO_RECORDED_ACTION = {
  nudge: (commit, { entries }) =>
    entries.forEach(({ layerId, operationId, previousValue }) =>
      previousValue === null
        ? commit('removeOperation', { layerId, operationId })
        : commit('updateOperation', { layerId, operationId, value: previousValue })
    ),

  duplicate: (commit, { layerIds }) => commit('removeLayers', layerIds),

  delete: (commit, { entries }) => commit('reinsertLayers', entries),

  tone: (commit, { layerId, previousGroup }) => commit('setLayerGroup', { layerId, group: previousGroup }),

  add: (commit, { layerId }) => commit('removeLayers', [layerId]),

  // The whole previous ordering, not a from/to pair: reconstructing a
  // permutation from one index pair is only correct for adjacent swaps.
  move: (commit, { previousOrder }) => commit('reorderLayers', previousOrder),

  // Restoring the flag alone is not enough. Per-layer `group` and the tray's
  // sort order both hang off duotone, so a layer would come back in a tone the
  // customer never saw.
  duotone: (commit, { previousDuotone, previousGroups }) => {
    commit('setDuotone', previousDuotone)
    Object.entries(previousGroups).forEach(([layerId, group]) => commit('setLayerGroup', { layerId, group }))
  },

  operationAdd: (commit, { layerId, operationId }) => commit('removeOperation', { layerId, operationId }),

  operationUpdate: (commit, { layerId, operationId, previousValue }) =>
    commit('updateOperation', { layerId, operationId, value: previousValue }),

  // Operations are ordered and the order affects the render, so reinserting at
  // the end is not a reversal.
  operationRemove: (commit, { layerId, operation, index }) =>
    commit('reinsertOperation', { layerId, operation, index }),

  // A scale or rotation drag can cross the value at which its operation stops
  // existing, so within one gesture the same control may update, remove and then
  // add. Restoring the layer's whole operation list is what lets a single entry
  // cover all three — an entry pinned to one operation id cannot, because the id
  // changes when the operation is re-created mid-drag.
  operationRun: (commit, { layerId, previousOperations }) =>
    commit('setLayerOperations', { layerId, operations: previousOperations }),

  method: (commit, { layerId, previousMethod, previousCutoutValue }) =>
    commit('setLayerMethod', { layerId, method: previousMethod, cutoutValue: previousCutoutValue }),

  libraryCutout: (commit, { layerId, previousMatch }) =>
    commit('setLayerLibraryCutout', { layerId, matchLibraryCutout: previousMatch }),

  // One entry for the whole snap, so a preset costs one Undo. Restoring the
  // operations wholesale is what lets a single entry cover both the nudges it
  // cleared and the position it set.
  snapPosition: (commit, { layerId, previousOperations, previousPositionType, previousX, previousY }) => {
    commit('setLayerOperations', { layerId, operations: previousOperations })
    commit('setLayerPosition', {
      layerId,
      positionType: previousPositionType,
      positionX: previousX,
      positionY: previousY
    })
  },

  position: (commit, { layerId, previousPositionType, previousX, previousY }) =>
    commit('setLayerPosition', {
      layerId,
      positionType: previousPositionType,
      positionX: previousX,
      positionY: previousY
    })
}

// Whether a gesture ended where it began. A slider dragged away and back, or a
// nudge cancelled out within one gesture, changed nothing, so its entry is
// dropped on close rather than pushed — pressing Undo afterwards should reverse
// whatever came before the gesture.
const GESTURE_IS_NOOP = {
  nudge: (state, { entries }) =>
    entries.every(({ layerId, operationId, previousValue }) => {
      const op = findOperation(state, layerId, operationId)
      return previousValue === null ? !op : op && op.value === previousValue
    }),

  operationUpdate: (state, { layerId, operationId, previousValue }) => {
    const op = findOperation(state, layerId, operationId)
    return !!op && op.value === previousValue
  },

  // Compared by type and value, not by identity. A drag that crosses the
  // boundary and comes back destroys the operation and creates a new one with a
  // fresh id, so an id-sensitive comparison would call an unchanged composition
  // changed. Ids are internal bookkeeping; the customer sees type and value.
  operationRun: (state, { layerId, previousOperations }) => {
    const layer = state.layers.find((l) => l.id === layerId)
    if (!layer || layer.operations.length !== previousOperations.length) return false
    return layer.operations.every(
      (op, i) => op.type === previousOperations[i].type && op.value === previousOperations[i].value
    )
  },

  method: (state, { layerId, previousMethod, previousCutoutValue }) => {
    const layer = state.layers.find((l) => l.id === layerId)
    return !!layer && layer.method === previousMethod && layer.cutoutValue === previousCutoutValue
  }
}

function findOperation(state, layerId, operationId) {
  const layer = state.layers.find((l) => l.id === layerId)
  return layer && layer.operations.find((o) => o.id === operationId)
}

// A gesture is a run of changes the customer experiences as one movement: a held
// arrow key, or a slider dragged in one motion. They share an entry so one Undo
// reverses the whole thing.
//
// The entry keeps the value in force when it OPENED and never takes a later one —
// that is what makes Undo land before the gesture rather than one step back into
// it. Capturing `previousValue` on every change instead is the failure this is
// written to avoid; it passes a click-test and breaks on a real drag.
function record(commit, state, entry, gestureKey = null) {
  const last = state.history[state.history.length - 1]

  if (gestureKey && last && last.open && last.gestureKey === gestureKey) return

  commit('closeGesture')
  commit('pushHistory', { ...entry, open: !!gestureKey, gestureKey })
}

export default {
  namespaced: true,

  state: {
    layers: [],
    svgPreview: null,
    selectionOutlines: [],
    isLoading: false,
    error: null,
    // An array rather than a Set: Vue 2 doesn't track Set mutations, so a Set
    // here renders a tray that never updates.
    selectedLayerIds: [],
    // The row a range is measured from. Set by a plain click and by a toggle
    // that adds; never moved by a range selection.
    selectionAnchorId: null,
    // Duotone splits the composition into two tones. Off unless turned on, and
    // off for every draft written before the feature existed — a composition
    // nobody opted in is a single tone and renders as one shape.
    duotone: false,

    // Ordered undo history, oldest first, bounded at MAX_HISTORY. Entries are
    // deltas rather than snapshots, and the last one may be `open` while a
    // gesture (a held key, a slider drag) is still in progress.
    history: [],
    viggo: null,
    viggoReady: false,
    share: null,
    modalOpen: false,
    draftKey: DEFAULT_DRAFT_KEY
  },

  getters: {
    layers: (state) => state.layers,
    svgPreview: (state) => state.svgPreview,
    selectionOutlines: (state) => state.selectionOutlines,
    isLoading: (state) => state.isLoading,
    error: (state) => state.error,
    viggoReady: (state) => state.viggoReady,
    share: (state) => state.share,
    modalOpen: (state) => state.modalOpen,

    selectedLayerIds: (state) => state.selectedLayerIds,
    selectionAnchorId: (state) => state.selectionAnchorId,
    canUndo: (state) => state.history.length > 0,

    duotone: (state) => state.duotone,

    primaryLayers: (state) => layersInGroup(state.layers, PRIMARY),

    secondaryLayers: (state) => layersInGroup(state.layers, SECONDARY),

    // Deliberately not the same as "duotone is on": a second shape is only
    // emitted when there is something in the secondary tone to emit. Everything
    // downstream — preview, save, share, submit — keys off this rather than the
    // toggle, so an empty secondary produces no second shape anywhere.
    hasSecondaryTone: (state) => state.duotone && layersInGroup(state.layers, SECONDARY).length > 0,

    selectedLayers: (state) => state.layers.filter((l) => state.selectedLayerIds.includes(l.id)),

    hasMultipleSelection: (state) => state.selectedLayerIds.length > 1,

    isLayerSelected: (state) => (layerId) => state.selectedLayerIds.includes(layerId),

    // Both of these resolve only when exactly one layer is selected. Every
    // per-layer control already guards on `selectedLayer`, so narrowing it here
    // is what makes scale, rotation, stroke, flip and the position presets
    // unavailable to a group without a new check at each of those sites.
    selectedLayerId: (state) => (state.selectedLayerIds.length === 1 ? state.selectedLayerIds[0] : null),

    selectedLayer: (state) => {
      if (state.selectedLayerIds.length !== 1) return null
      return state.layers.find((l) => l.id === state.selectedLayerIds[0]) || null
    },

    combineOperations: (state) => buildCombineOperations(state.layers)
  },

  mutations: {
    addLayer(state, iconData) {
      // Explicitly primary rather than relying on which end of the array a push
      // lands at. The two happen to agree — the primary side is the end — but
      // the tone is the thing that matters and shouldn't be inferred.
      state.layers.push(createLayer({ ...iconData, group: PRIMARY }))
    },

    copyLayers(state, layerIds) {
      const { layers, copyIds } = copyLayersInList(state.layers, layerIds)
      state.layers = layers
      state.selectedLayerIds = copyIds
      state.selectionAnchorId = copyIds[copyIds.length - 1] || null
    },

    moveLayer(state, { layerId, direction }) {
      state.layers = moveLayerInList(state.layers, layerId, direction)
    },

    removeLayers(state, layerIds) {
      state.layers = state.layers.filter((layer) => !layerIds.includes(layer.id))
      state.selectedLayerIds = state.selectedLayerIds.filter((id) => !layerIds.includes(id))
      if (layerIds.includes(state.selectionAnchorId)) {
        state.selectionAnchorId = null
      }
    },

    // Puts deleted layers back where they were. Entries are re-inserted in
    // ascending index order so each splice lands at the index it was taken from.
    reinsertLayers(state, entries) {
      const next = [...state.layers]
      ;[...entries]
        .sort((a, b) => a.index - b.index)
        .forEach(({ layer, index }) => next.splice(index, 0, layer))
      state.layers = next
    },

    clearLayers(state) {
      state.layers = []
      state.selectedLayerIds = []
      state.selectionAnchorId = null
      // Clearing is a reset, not an edit: the history goes with the composition
      // and the clear itself is not undoable.
      state.history = []
      // Duotone belongs to the composition, so it goes when the composition does.
      state.duotone = false
    },

    // A restored composition is the baseline Undo cannot step behind.
    restoreLayers(state, layers) {
      state.layers = layers
      state.selectedLayerIds = []
      state.selectionAnchorId = null
      state.history = []
    },

    setDraftKey(state, key) {
      state.draftKey = key
    },

    setSelection(state, { ids, anchorId }) {
      state.selectedLayerIds = ids
      state.selectionAnchorId = anchorId
    },

    setDuotone(state, enabled) {
      state.duotone = enabled
    },

    setLayerGroup(state, { layerId, group }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer || layer.group === group) return
      layer.group = group
      // Re-sorting here is what keeps the divider derivable: a layer that
      // changed tone is moved to its tone's side in the same breath.
      state.layers = sortLayersByGroup(state.layers)
    },

    // Oldest-first eviction at the bound. The customer isn't told; they simply
    // can't step back past MAX_HISTORY.
    pushHistory(state, entry) {
      state.history.push(entry)
      if (state.history.length > MAX_HISTORY) state.history.shift()
    },

    popHistory(state) {
      state.history.pop()
    },

    clearHistory(state) {
      state.history = []
    },

    // Marks an in-progress gesture finished. Deliberately does NOT judge whether
    // the gesture changed anything: `record` calls this *after* the incoming
    // action has already mutated state, so a state-dependent test here reads the
    // wrong world. Dropping an unchanged gesture is `discardOpenGestureIfUnchanged`'s
    // job, and only the explicit gesture-end path may call it.
    closeGesture(state) {
      const entry = state.history[state.history.length - 1]
      if (entry && entry.open) entry.open = false
    },

    // A gesture that ended where it began records nothing. Safe only while the
    // operations it tracks are still in the state it is compared against — which
    // is true on the control's own `change`/`blur`, and false once another action
    // has removed them.
    discardOpenGestureIfUnchanged(state) {
      const entry = state.history[state.history.length - 1]
      if (!entry || !entry.open) return

      const isNoop = GESTURE_IS_NOOP[entry.kind]
      if (isNoop && isNoop(state, entry)) state.history.pop()
    },

    // Drops selection ids that no longer name a layer.
    pruneSelection(state) {
      const ids = new Set(state.layers.map((l) => l.id))
      state.selectedLayerIds = state.selectedLayerIds.filter((id) => ids.has(id))
      if (!ids.has(state.selectionAnchorId)) state.selectionAnchorId = null
    },

    reorderLayers(state, previousOrder) {
      const byId = new Map(state.layers.map((l) => [l.id, l]))
      state.layers = previousOrder.map((id) => byId.get(id)).filter(Boolean)
    },

    // Mirrors reinsertLayers: the index matters because operation order affects
    // the rendered result.
    setLayerOperations(state, { layerId, operations }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) layer.operations = operations
    },

    reinsertOperation(state, { layerId, operation, index }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return
      const next = [...layer.operations]
      next.splice(index, 0, operation)
      layer.operations = next
    },

    addOperation(state, { layerId, type, value }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) {
        layer.operations.push(createOperation({ type, value }))
      }
    },

    updateOperation(state, { layerId, operationId, value }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) {
        const op = layer.operations.find((o) => o.id === operationId)
        if (op) {
          op.value = value
        }
      }
    },

    setLayerMethod(state, { layerId, method, cutoutValue }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) {
        layer.method = method
        layer.cutoutValue = cutoutValue
      }
    },

    setLayerLibraryCutout(state, { layerId, matchLibraryCutout }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) layer.matchLibraryCutout = matchLibraryCutout
    },

    setLayerPosition(state, { layerId, positionType, positionX, positionY }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) {
        layer.positionType = positionType
        layer.positionX = positionX
        layer.positionY = positionY
      }
    },

    removeOperation(state, { layerId, operationId }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (layer) {
        layer.operations = layer.operations.filter((o) => o.id !== operationId)
      }
    },

    setSvgPreview(state, svg) {
      state.svgPreview = svg
    },

    setSelectionOutlines(state, outlines) {
      state.selectionOutlines = outlines
    },

    setLoading(state, isLoading) {
      state.isLoading = isLoading
    },

    setError(state, error) {
      state.error = error
    },

    setViggo(state, viggo) {
      state.viggo = viggo
      state.viggoReady = true
    },

    setShare(state, share) {
      state.share = share
    },

    setModalOpen(state, isOpen) {
      state.modalOpen = isOpen
    }
  },

  actions: {
    async initViggo({ commit, state }) {
      if (state.viggoReady) return
      try {
        const viggo = await loadViggo()
        commit('setViggo', viggo)
      } catch (e) {
        commit('setError', 'Failed to load Viggo')
      }
    },

    setModalOpen({ commit }, isOpen) {
      commit('setModalOpen', isOpen)
    },

    addLayer({ commit, dispatch, state }, iconData) {
      if (state.layers.length >= MAX_LAYERS) return
      commit('addLayer', iconData)
      const newLayer = state.layers[state.layers.length - 1]
      commit('setSelection', { ids: [newLayer.id], anchorId: newLayer.id })
      record(commit, state, { kind: 'add', layerId: newLayer.id })
      dispatch('debouncedRender')
    },

    // Copies land directly after their sources and become the selection, so the
    // group can be moved straight away. All-or-nothing against the layer limit:
    // a partial copy would silently break up the group being duplicated.
    duplicateLayers({ commit, dispatch, state }, layerIds) {
      const ids = orderedLayerIds(state.layers, layerIds)
      if (ids.length === 0) return
      if (state.layers.length + ids.length > MAX_LAYERS) {
        commit('setError', `That would take the canvas past ${MAX_LAYERS} layers. Remove a layer and try again.`)
        return
      }
      commit('copyLayers', ids)
      record(commit, state, { kind: 'duplicate', layerIds: [...state.selectedLayerIds] })
      // No separate outline pass: the render re-cuts them, and asking twice
      // would double the tracing for every selected layer.
      dispatch('debouncedRender')
    },

    removeLayers({ commit, dispatch, state }, layerIds) {
      const ids = orderedLayerIds(state.layers, layerIds)
      if (ids.length === 0) return
      // Captured before the removal so Undo can put each layer back where it
      // was rather than on top of the stack.
      const entries = ids.map((id) => ({
        layer: state.layers.find((l) => l.id === id),
        index: state.layers.findIndex((l) => l.id === id)
      }))
      commit('removeLayers', ids)
      record(commit, state, { kind: 'delete', entries })
      dispatch('debouncedRender')
    },

    moveLayer({ commit, dispatch, state }, { layerId, direction }) {
      const previousOrder = state.layers.map((l) => l.id)
      commit('moveLayer', { layerId, direction })
      // A move at either end of the stack is a no-op; nothing to reverse.
      if (state.layers.map((l) => l.id).join() === previousOrder.join()) return
      record(commit, state, { kind: 'move', previousOrder })
      dispatch('debouncedRender')
    },

    clearLayers({ commit, state }) {
      commit('clearLayers')
      commit('setSvgPreview', null)
      commit('setError', null)
      clearDraft(state.draftKey)
    },

    // Repopulate the standalone page's canvas from its persisted draft
    // (sign-in round-trips, Kit creation detours). No-op when the canvas
    // already has layers, so an in-progress composition wins over the draft.
    restoreDraft({ state, commit, dispatch }) {
      commit('setDraftKey', DEFAULT_DRAFT_KEY)
      if (state.layers.length > 0) return
      const draft = readDraft(DEFAULT_DRAFT_KEY)
      if (!draft) return
      // restoreLayers empties the history, so the saved one is applied after it.
      commit('restoreLayers', draft.layers)
      commit('setDuotone', draft.duotone)
      draft.history.forEach((entry) => commit('pushHistory', entry))
      dispatch('debouncedRender')
    },

    // Modal entry point: each seed icon keeps its own draft, so closing and
    // reopening the wizard on the same icon restores the work in progress.
    // With no draft, fall through to the fresh clear + seed.
    seedFromIcon({ commit, dispatch }, iconData) {
      commit('setDraftKey', iconDraftKey(iconData.iconName, iconData.prefix))
      const draft = readDraft(iconDraftKey(iconData.iconName, iconData.prefix))
      if (draft) {
        commit('restoreLayers', draft.layers)
        commit('setDuotone', draft.duotone)
        draft.history.forEach((entry) => commit('pushHistory', entry))
        dispatch('debouncedRender')
        return
      }
      dispatch('clearLayers')
      dispatch('addLayer', iconData)
    },

    // `mode` is 'plain' (this row alone, or nothing if it was already the sole
    // selection), 'range' (from the anchor to here) or 'toggle' (add/remove
    // this row, leaving the rest alone).
    // Turning duotone on never moves a layer: every layer is already primary
    // unless someone put it in the secondary tone, and turning it off keeps the
    // tones rather than flattening them, so the toggle is safe to experiment
    // with.
    setDuotone({ commit, dispatch, state }, enabled) {
      if (state.duotone === enabled) return
      const previousDuotone = state.duotone
      // The flag alone is not enough to reverse this: per-layer tone and the
      // tray's sort order both hang off duotone.
      const previousGroups = state.layers.reduce((acc, l) => ({ ...acc, [l.id]: l.group }), {})

      commit('setDuotone', enabled)
      record(commit, state, { kind: 'duotone', previousDuotone, previousGroups })
      dispatch('debouncedRender')
    },

    // Recorded like a nudge, duplicate or delete. Without a record, Undo after
    // a tone change doesn't reverse it — and worse, reaches past it to reverse
    // whatever happened before, which is the one thing the single-slot design
    // exists to prevent.
    setLayerGroup({ commit, dispatch, state }, { layerId, group }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer || layer.group === group) return
      const previousGroup = layer.group

      commit('setLayerGroup', { layerId, group })
      record(commit, state, { kind: 'tone', layerId, previousGroup })
      dispatch('debouncedRender')
    },

    clearSelection({ commit, dispatch }) {
      commit('setSelection', { ids: [], anchorId: null })
      dispatch('renderSelectionOutlines')
    },

    // The anchor lands on the last layer so a shift-click straight afterwards
    // measures its range from one end of the list rather than from wherever the
    // previous selection happened to leave it.
    selectAllLayers({ commit, dispatch, state }) {
      if (state.layers.length === 0) return
      const ids = state.layers.map((l) => l.id)
      commit('setSelection', { ids, anchorId: ids[ids.length - 1] })
      dispatch('renderSelectionOutlines')
    },

    selectLayer({ commit, dispatch, state }, { layerId, mode = 'plain' }) {
      const current = state.selectedLayerIds
      let ids
      let anchorId

      if (mode === 'range') {
        const range = layerIdRange(state.layers, state.selectionAnchorId, layerId)
        if (range.length > 0) {
          // A range never moves the anchor, so the next range is measured from
          // the same row.
          ids = range
          anchorId = state.selectionAnchorId
        } else {
          ids = [layerId]
          anchorId = layerId
        }
      } else if (mode === 'toggle') {
        if (current.includes(layerId)) {
          ids = current.filter((id) => id !== layerId)
          anchorId = state.selectionAnchorId
        } else {
          ids = orderedLayerIds(state.layers, [...current, layerId])
          anchorId = layerId
        }
      } else if (current.length === 1 && current[0] === layerId) {
        ids = []
        anchorId = null
      } else {
        ids = [layerId]
        anchorId = layerId
      }

      commit('setSelection', { ids, anchorId })
      // Selection alone changes no geometry, so nothing else would re-cut the
      // traces for the newly selected layers.
      dispatch('renderSelectionOutlines')
    },

    // One delta, applied identically to every selected layer, in a single
    // dispatch — the composition keeps its shape, and a four-layer nudge costs
    // what a one-layer nudge costs.
    nudgeSelection({ commit, dispatch, state, getters }, { type, step }) {
      const layers = getters.selectedLayers
      if (layers.length === 0) return

      const entries = []
      layers.forEach((layer) => {
        const existing = layer.operations.find((op) => op.type === type)
        if (existing) {
          entries.push({ layerId: layer.id, operationId: existing.id, previousValue: existing.value })
          commit('updateOperation', { layerId: layer.id, operationId: existing.id, value: existing.value + step })
        } else {
          commit('addOperation', { layerId: layer.id, type, value: step })
          const created = layer.operations[layer.operations.length - 1]
          entries.push({ layerId: layer.id, operationId: created.id, previousValue: null })
        }
      })

      // A run of same-direction nudges on the same selection is one gesture, so
      // one Undo reverses a whole held key rather than a single repeat. The key
      // changes when the direction or the selection does, which starts a new
      // entry (FR-008).
      record(commit, state, { kind: 'nudge', entries }, `nudge:${type}:${layers.map((l) => l.id).join()}`)
      dispatch('debouncedRender')
    },

    // Ends an in-progress gesture. Called from the controls' own end events —
    // `change` on a slider, `blur` on a number field — because there is no other
    // signal that a drag finished.
    endGesture({ commit }) {
      commit('discardOpenGestureIfUnchanged')
      commit('closeGesture')
    },

    // Reverses one step. With nothing recorded it does nothing at all — there is
    // no fallback that peels an operation or drops a layer. That fallback used to
    // exist and it deleted work the customer never added, which is the whole
    // reason for the empty-history guard here rather than a cleverer default.
    undoLastAction({ commit, dispatch, state }) {
      // An in-progress gesture is undone as the one movement it is — unless it
      // ended where it began, in which case Undo should reach past it.
      commit('discardOpenGestureIfUnchanged')
      commit('closeGesture')

      const entry = state.history[state.history.length - 1]
      if (!entry) return

      const undo = UNDO_RECORDED_ACTION[entry.kind]
      if (!undo) return

      undo(commit, entry)
      commit('popHistory')

      // Reversing an add removes a layer that was very likely selected; a
      // dangling id leaves the controls disabled with no visible cause.
      commit('pruneSelection')
      dispatch('debouncedRender')
    },

    // Everything below records its own history entry.
    addOperation({ commit, dispatch, state }, { layerId, type, value }) {
      commit('addOperation', { layerId, type, value })
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return
      const created = layer.operations[layer.operations.length - 1]
      record(commit, state, { kind: 'operationAdd', layerId, operationId: created.id })
      dispatch('debouncedRender')
    },

    updateOperation({ commit, dispatch, state }, { layerId, operationId, value }) {
      const existing = findOperation(state, layerId, operationId)
      if (!existing || existing.value === value) return
      const previousValue = existing.value

      commit('updateOperation', { layerId, operationId, value })
      // A slider drag emits a value per pixel. They share one entry, closed by
      // the control's `change`/`blur`, so one Undo lands before the drag.
      record(
        commit,
        state,
        { kind: 'operationUpdate', layerId, operationId, previousValue },
        `operation:${layerId}:${operationId}`
      )
      dispatch('debouncedRender')
    },

    // The single entry point for a control that owns one operation type on a
    // layer — scale, rotation. It decides add/update/remove itself so the caller
    // does not, and records ONE gesture-grouped entry across all three. Without
    // this, a drag crossing 100% scale or 0 degrees split into an update, a
    // remove, an add and another update: four Undo presses for one motion, which
    // is exactly what the gesture rule exists to prevent.
    setOperationValue({ commit, dispatch, state }, { layerId, type, value, remove = false }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return

      const existing = layer.operations.find((op) => op.type === type)
      // FR-010: nothing to remove, or the value is already what it would become.
      if (remove && !existing) return
      if (!remove && existing && existing.value === value) return

      const previousOperations = layer.operations.map((op) => ({ ...op }))

      if (remove) commit('removeOperation', { layerId, operationId: existing.id })
      else if (existing) commit('updateOperation', { layerId, operationId: existing.id, value })
      else commit('addOperation', { layerId, type, value })

      record(commit, state, { kind: 'operationRun', layerId, previousOperations }, `operation:${type}:${layerId}`)
      dispatch('debouncedRender')
    },

    removeOperation({ commit, dispatch, state }, { layerId, operationId }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return
      const index = layer.operations.findIndex((o) => o.id === operationId)
      if (index === -1) return
      const operation = layer.operations[index]

      commit('removeOperation', { layerId, operationId })
      record(commit, state, { kind: 'operationRemove', layerId, operation, index })
      dispatch('debouncedRender')
    },

    updateLayerMethod({ commit, dispatch, state }, { layerId, method, cutoutValue }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return
      const previousMethod = layer.method
      const previousCutoutValue = layer.cutoutValue

      // FR-010. The cutout field's blur handler re-applies whatever value is
      // already showing, so without this a committed edit recorded twice: the
      // real change, then a no-op. The second cost an Undo press that changed
      // nothing visible.
      if (previousMethod === method && previousCutoutValue === cutoutValue) return

      commit('setLayerMethod', { layerId, method, cutoutValue })
      // Dragging the cutout stroke, or typing into its field, is one gesture the
      // same way a scale drag is — every keystroke emits its own value, and
      // "4.5" would otherwise be three steps. Changing the method itself is
      // discrete and keeps its own step.
      const gestureKey = previousMethod === method ? `cutout:${layerId}` : null
      record(commit, state, { kind: 'method', layerId, previousMethod, previousCutoutValue }, gestureKey)
      dispatch('debouncedRender')
    },

    setMatchLibraryCutout({ commit, dispatch, state }, { layerId, matchLibraryCutout }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer?.cutoutPath || !!layer.matchLibraryCutout === matchLibraryCutout) return
      const previousMatch = !!layer.matchLibraryCutout

      commit('setLayerLibraryCutout', { layerId, matchLibraryCutout })
      record(commit, state, { kind: 'libraryCutout', layerId, previousMatch })
      dispatch('debouncedRender')
    },

    // Snapping to a position preset is ONE gesture to the customer, so it is one
    // history entry — not one per nudge operation it happens to clear plus one
    // for the position. Without this a corner preset cost up to five Undos and
    // repainted the canvas in positions nobody chose on the way back.
    snapPosition({ commit, dispatch, state }, { layerId, positionType, positionX = 0, positionY = 0 }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return

      const nudged = layer.operations.filter((op) => NUDGE_TYPES.includes(op.type))
      const samePosition =
        layer.positionType === positionType && layer.positionX === positionX && layer.positionY === positionY
      if (nudged.length === 0 && samePosition) return

      const previousOperations = layer.operations.map((op) => ({ ...op }))
      const previousPositionType = layer.positionType
      const previousX = layer.positionX
      const previousY = layer.positionY

      nudged.forEach((op) => commit('removeOperation', { layerId, operationId: op.id }))
      commit('setLayerPosition', { layerId, positionType, positionX, positionY })
      record(commit, state, {
        kind: 'snapPosition',
        layerId,
        previousOperations,
        previousPositionType,
        previousX,
        previousY
      })
      dispatch('debouncedRender')
    },

    updateLayerPosition({ commit, dispatch, state }, { layerId, positionType, positionX, positionY }) {
      const layer = state.layers.find((l) => l.id === layerId)
      if (!layer) return
      const previousPositionType = layer.positionType
      const previousX = layer.positionX
      const previousY = layer.positionY

      commit('setLayerPosition', { layerId, positionType, positionX, positionY })
      record(commit, state, { kind: 'position', layerId, previousPositionType, previousX, previousY })
      dispatch('debouncedRender')
    },

    debouncedRender({ dispatch, state }) {
      let fn = _debouncedRenderByDispatch.get(dispatch)
      if (!fn) {
        // Every layer mutation funnels through here, so it doubles as the
        // draft-persistence point.
        // Leading edge so a single edit draws straight away instead of waiting
        // out the interval; the trailing call collapses a burst rather than
        // queueing a combine per keypress.
        //
        // `maxWait` is what keeps the canvas moving while a key is held. Auto-
        // repeat arrives every ~30ms, well inside the 300ms window, so without
        // it the trailing call is pushed back on every keypress and nothing
        // draws between the press and the release — the composition stays
        // correct, but the canvas reads as frozen. Measured at the ceiling, the
        // old configuration drew twice for a 100-keypress hold.
        //
        // The window has to be WIDER THAN A RENDER, which is why it is 700 and
        // not the original 300. A render at the 20-layer ceiling blocks the main
        // thread for ~350ms (measured: ~353ms duotone on, ~271ms off, ~126ms
        // with one layer selected). When a render blocks longer than the window,
        // it pushes the next queued keypress outside it, so every keypress
        // becomes a fresh burst and the leading edge fires for each one — the
        // debounce stops collapsing anything. Measured at the ceiling with 100
        // keypresses: `300 + maxWait 600` produced 100 renders, one per press,
        // gaps of ~335ms — the render duration, not the interval. 700 produces
        // ~800ms gaps, bounded by the timer as intended.
        //
        // Re-measure before changing either number, and keep the window above
        // the render cost. That cost already doubled once, when duotone's second
        // combine pass landed.
        fn = debounce(() => {
          dispatch('renderPreview')
          persistDraft(state.draftKey, state.layers, state.duotone, state.history)
        }, RENDER_DEBOUNCE_MS, { leading: true, maxWait: RENDER_MAX_WAIT_MS })
        _debouncedRenderByDispatch.set(dispatch, fn)
      }
      fn()
    },


    // Resolves viggo (loading it if needed) and delegates the combine — plus
    // its contour→outer fallback — to `combineWithFallback`. Returns
    // { width, height, path }. Defaults to the whole composition; the selection
    // outline passes a single operand instead.
    async runCombine({ getters, state, dispatch }, operations) {
      if (!state.viggoReady) {
        await dispatch('initViggo')
      }
      const viggo = state.viggo
      if (!viggo || !viggo.SvgIcon) {
        throw new Error('Viggo not available')
      }

      // An explicit operand list is a caller asking for one specific combine —
      // the selection outline does this — and never wants the duotone split.
      if (operations) return combineWithFallback(viggo, operations)

      if (!getters.hasSecondaryTone) {
        // Single tone: byte for byte what this returned before duotone existed.
        return { ...combineWithFallback(viggo, getters.combineOperations), pathSecondary: null }
      }

      // Two passes. The primary composes on its own, then the secondary
      // composes with that result appended as a subtract operand — which is
      // what guarantees the tones never overlap, rather than relying on the
      // shapes happening not to.
      //
      // Both passes go through `combineWithFallback` rather than calling viggo
      // directly, so each keeps the contour→outer fallback and the deliberate
      // crop to the canvas.
      // The primary layers are replayed into the second pass so a stroke on a
      // primary layer cuts the secondary the way it cuts its own tone —
      // otherwise the secondary fills the gap the stroke just opened and the
      // stroke reads as doing nothing. Replaying all of them, not just the
      // stroked ones, is what keeps the tones disjoint: the replay cuts among
      // itself exactly as pass one did, so the primary geometry it adds is
      // precisely what the final subtract takes back out. Drop a layer from
      // the replay and whatever a Subtract layer removed from it leaks into
      // the secondary.
      const primary = combineWithFallback(viggo, buildCombineOperations(getters.primaryLayers))
      const secondary = combineWithFallback(viggo, [
        ...buildCombineOperations(getters.secondaryLayers),
        ...buildCombineOperations(getters.primaryLayers),
        buildSubtractOperation(primary.path)
      ])

      return { width: primary.width, height: primary.height, path: primary.path, pathSecondary: secondary.path }
    },

    // Traces each selected layer on its own so the canvas can show which layers
    // the controls are driving. Each is a second combine over a single operand,
    // giving paths in the same coordinate space as the composed preview.
    async renderSelectionOutlines({ commit, getters, dispatch }) {
      const layers = getters.selectedLayers
      if (layers.length === 0) {
        commit('setSelectionOutlines', [])
        return
      }

      // Tracing a shape strips the layer's method, so these combines carry no
      // cutout and cost about a millisecond each — and they're independent, so
      // they run together rather than queueing behind one another.
      const outlines = await Promise.all(layers.map(async (layer) => {
        try {
          const { width, height, path } = await dispatch('runCombine', [buildOutlineOperation(layer)])
          // The canvas draws the cutout stroke's edge from this same path, so
          // all it needs besides the shape is how far the cutout reaches.
          const cutPath = usesLibraryCutout(layer)
            ? (await dispatch('runCombine', [buildLibraryCutoutOperation(layer)])).path
            : null
          return { layerId: layer.id, width, height, path, cutPath, strokeOffset: strokeOffsetForLayer(layer) }
        } catch (e) {
          // An outline is decoration, and each layer catches its own failure:
          // one shape viggo can't combine alone goes untraced rather than
          // costing every other selected layer its outline.
          return null
        }
      }))

      commit('setSelectionOutlines', outlines.filter(Boolean))
    },

    async renderPreview({ commit, getters, dispatch }) {
      const layers = getters.layers
      if (layers.length === 0) {
        commit('setSvgPreview', null)
        commit('setSelectionOutlines', [])
        commit('setError', null)
        return
      }

      commit('setLoading', true)

      try {
        const { width, height, path, pathSecondary } = await dispatch('runCombine')
        commit('setSvgPreview', buildIconSvgMarkup({ path, secondaryPath: pathSecondary, width, height }))
        commit('setError', null)
      } catch (e) {
        commit('setError', `Failed to render preview: ${e.message || e}`)
      } finally {
        commit('setLoading', false)
      }

      // The traced layers move with the composition, so they are re-cut
      // whenever the preview is.
      await dispatch('renderSelectionOutlines')
    },

    async createShare({ commit, getters, dispatch }) {
      const layers = getters.layers
      if (layers.length === 0) {
        throw new Error('Nothing to share — add a layer first')
      }

      const { width, height, path, pathSecondary } = await dispatch('runCombine')
      const name = shareNameFromLayers(layers)

      const response = await create({
        url: '/api/iconwizard/shares',
        type: 'icon-wizard-share',
        // Omitted for a single tone. The changeset takes a null but rejects a
        // blank, so absent has to mean absent here too.
        data: { name, width, height, path, ...(pathSecondary ? { pathSecondary } : {}) }
      })

      const row = response?.data?.data
      if (!row) {
        throw new Error('Share response was empty')
      }

      const share = {
        token: row.id,
        name: row.attributes.name,
        width: row.attributes.width,
        height: row.attributes.height,
        path: row.attributes.path,
        pathSecondary: row.attributes['path-secondary'] ?? null,
        shareUrl: row.attributes['share-url'],
        imageUrl: row.attributes['image-url']
      }

      commit('setShare', share)
      return share
    }
  }
}
